import { createApi } from '@reduxjs/toolkit/query/react';

import { generateBaseQuseryWithReAuth } from '@/store/baseQueryReauth';
import { ResponseError } from '@/types/Error';
import { CreditCardPayload, Payment } from '@/types/Payment';
import { SuccessResponse } from '@/types/Success';

import { shoppingCartApi } from '../../shoppingCart';

export const paymentApi = createApi({
  reducerPath: 'user/account/paymentApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: `account` }),
  tagTypes: ['GetAllPaymentMethods', 'AddPaymentMethod'],
  endpoints: builder => ({
    getAllPaymentMethods: builder.query<Payment[], string | number>({
      query: userId => ({ url: `/${userId}/payments`, next: { tags: ['GetAllPaymentMethods'] } }),
      transformResponse: (response: { data: Payment[] }, meta, arg) => response.data,
      providesTags: ['GetAllPaymentMethods'],
    }),
    addPaymentMethod: builder.mutation<
      SuccessResponse<unknown>,
      { userId: string | number; paymentMethod: Omit<CreditCardPayload, 'type'> }
    >({
      query: ({ userId, paymentMethod }) => ({
        url: `/${userId}/payments`,
        method: 'POST',
        body: JSON.stringify(paymentMethod),
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['GetAllPaymentMethods'],
    }),
    deletePayment: builder.mutation<SuccessResponse<unknown>, { paymentType: number }>({
      query: ({ paymentType }) => ({
        url: `/DeleteCreditCard?type=${paymentType}`,
        method: 'POST',
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(shoppingCartApi.util.invalidateTags(['PaymentsDetails']));
      },
    }),
  }),
});

export const { useDeletePaymentMutation, useAddPaymentMethodMutation } = paymentApi;
